<template>
  <div class="relative flex w-full flex-col gap-2 rounded-lg bg-white p-3">
    <Badge variant="warning" class="self-start">{{ t('unsent') }}</Badge>
    <p class="break-words">{{ damage.description }}</p>
    <p v-if="damage.lastError" class="text-xs text-red-600">{{ damage.lastError }}</p>
    <CVButton
      v-if="$can('delete', 'Damage')"
      variant="ghost"
      size="sm"
      class="self-end"
      @click.prevent="handleDiscard"
    >
      <Trash2Icon class="size-4" />
      {{ t('discard') }}
    </CVButton>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { Trash2Icon } from 'lucide-vue-next';
import Badge from '@/components/Badge.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { Alert } from '@/services/alert.service';
import { evidenceUploadQueue } from '@/utils/evidence-upload-queue';
import type { UnsentDamageRow } from '@/hooks/use-unsent-damages';

const props = defineProps<{ damage: UnsentDamageRow }>();

const { t } = useI18n();

const handleDiscard = async () => {
  const { isConfirmed } = await Alert.fire({
    icon: 'warning',
    title: t('discardTitle'),
    text: t('discardText'),
    showCancelButton: true,
    confirmButtonText: t('discard'),
    cancelButtonText: t('cancel'),
  });
  if (isConfirmed) await evidenceUploadQueue.discardUnsentDamage(props.damage.id);
};
</script>

<i18n lang="json">
{
  "en": {
    "unsent": "Not sent yet",
    "discard": "Discard",
    "discardTitle": "Discard damage?",
    "discardText": "The damage and its photos and video are deleted from this device and never sent.",
    "cancel": "Cancel"
  },
  "de": {
    "unsent": "Noch nicht gesendet",
    "discard": "Verwerfen",
    "discardTitle": "Schaden verwerfen?",
    "discardText": "Der Schaden wird samt Fotos und Video von diesem Gerät gelöscht und nie gesendet.",
    "cancel": "Abbrechen"
  },
  "es": {
    "unsent": "Aún no enviado",
    "discard": "Descartar",
    "discardTitle": "¿Descartar daño?",
    "discardText": "El daño, sus fotos y su vídeo se borran de este dispositivo y nunca se envían.",
    "cancel": "Cancelar"
  }
}
</i18n>
